import { c as _c } from "react/compiler-runtime";
import Avatar from '@precis-digital/kurama/Avatar';
import BookIcon from '@precis-digital/kurama/icons/BookIcon';
import CopilotIcon from '@precis-digital/kurama/icons/CopilotIcon';
import FeedIcon from '@precis-digital/kurama/icons/FeedIcon';
import Flyout from '@precis-digital/kurama/Flyout';
import Frame, { FRAME_CONTENT_PADDING } from '@precis-digital/kurama/Frame';
import GlobalLoader from '@precis-digital/kurama/GlobalLoader';
import Grid from '@precis-digital/kurama/Grid';
import SideNavigation, { type NavContentItem } from '@precis-digital/kurama/SideNavigation';
import SwitchIcon from '@precis-digital/kurama/icons/SwitchIcon';
import TopNavigation from '@precis-digital/kurama/TopNavigation';
import Typography from '@precis-digital/kurama/Typography';
import { useTheme } from '@precis-digital/kurama/utils';
import { type NotificationVariant } from '@precis-digital/kurama/Notification';
import { useQueryUnreadNotificationsCount } from 'src/workspace/api';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import withPageAuthRequired from 'src/auth/WithPageAuthRequired';
import { useQueryUserProfile } from 'src/profile/api';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { type ClientFeatureType } from 'shared/api/clients';
import { AvatarWithLabel } from 'shared/components/AvatarWithLabel';
import ErrInvalidClient from 'shared/components/Error/InvalidClient';
import NoAccessToFeature from 'shared/components/Error/NoAccessToFeature';
import ProgressBarLoader from 'shared/components/Loader/ProgressBarLoader';
import { RetainDefaultQueryLink as Link, usePathname, useReload } from 'shared/components/Router';
import { useBreadcrumbs } from 'shared/hooks/useBreadcrumbs';
import config from 'shared/config';
import { INTERCOM_SPACE_IDS } from 'shared/constants/documentation';
import { useAuth } from 'shared/context/AuthContext';
import { useCurrentClient } from 'shared/context/ClientContext';
import { useCopilot } from 'shared/context/CopilotContext';
import ErrorReporterProvider from 'shared/context/ErrorReporterContext';
import { FEATURE_FLAGS, useFeatureFlag, useFeatureFlagClient } from 'shared/featureFlag';
import useDashboardDataPreloader from 'shared/hooks/useDashboardDataPreloader';
import { useHasClientFeatureAccess } from 'shared/hooks/useHasFeatureAccess';
import { useNavButtons } from 'shared/hooks/useNavButtons';
import { useShouldShowInternalFeatures } from 'shared/hooks/useShouldShowInternalFeatures';
import { boot as bootIntercom, openIntercomSpace, update as updateIntercom } from 'shared/intercom';
import { identify as logRocketIdentify } from 'shared/logRocket';
import { LOCAL_STORAGE_KEYS, get, store } from 'shared/lstore';
import 'shared/overScreens/register';
import { OverScreen, useOverscreenContext } from 'shared/overScreens/niceModalReact';
import { useTranslation } from 'shared/translations';
import withInternalFeatureAccessCheck from 'shared/withInternalFeatureAccessCheck';
import FlyoutContent from 'src/workspace/components/Flyout';
import CopilotDrawer from '../../copilot/components/CopilotDrawer';
import { useIsCopilotStandaloneChatPage } from '../../copilot/hooks/useIsCopilotStandaloneChatPage';
import { ErrorFallback } from '../ErrorFallback';
import NotFoundErrorBoundary from '../Error/NotFoundErrorBoundary';
import FlyoutTopLeftAdornment, { shouldShowFlyoutTopLeftAdornment } from './FlyoutTopLeftAdornment';
import { useDashboardFrameKeybindings } from './useDashboardFrameKeybindings';
import { buildNavContentItems, clearOverscreensOnRouteChange, formatNotificationBadgeValue } from './utils';
enum AvatarKind {
  label = 'label',
  image = 'image',
}
interface LinkProps {
  children: React.ReactNode;
  href?: string;
}
const HOME_PATH = '/';
const NAV_HEADER_LABEL = 'alvie';
const HELP_CENTER_LABEL = 'Help center';
const AVATAR_LABEL = 'je';
const STAGING_PROD_ENV = 'stagingprod';
const EMPTY_CLIENT_NAME = '';
const EMPTY_STRING = '';
const NOTIFICATION_VARIANT_FILLED = 'filled' as NotificationVariant;
const IS_NAV_FOOTER_EXPANDED = true;
const IS_NAV_EXPANDED = true;
const STAGING_WARNING_HEIGHT = '32px';
const STAGING_WARNING_WIDTH = '100%';
const STAGING_WARNING_TOP = '0px';
const STAGING_WARNING_Z_INDEX = 1000;
const STAGING_WARNING_TEXT = 'Warning! This is an in-development deployment targeting our production api. Please be careful with changes.';
const COPILOT_ARIA_LABEL = 'copilot';
const getPathNameIndexes = (pathname: string, navContentItems: NavContentItem[]): {
  primaryIndex: number | undefined;
  childIndex: number | undefined;
} => {
  let primaryIndex;
  let childIndex;
  for (let i = 0; i < navContentItems.length; i++) {
    const item = navContentItems[i];
    if ('path' in item) {
      if (pathname.startsWith(item.path)) {
        primaryIndex = i;
      }
    } else if (item.subItems != null) {
      for (let subIndex = 0; subIndex < item.subItems.length; subIndex++) {
        const subItem = item.subItems[subIndex];
        const firstTwoSegmentsOfItemPath = subItem.path.split('/').slice(0, 3).join('/');
        if (pathname.startsWith(firstTwoSegmentsOfItemPath)) {
          primaryIndex = i;
          childIndex = subIndex;
        }
      }
    }
  }
  return {
    primaryIndex,
    childIndex
  };
};
const FrameWithAuthAndInternalAccessCheck = withInternalFeatureAccessCheck(withPageAuthRequired(Frame, {
  onRedirecting: () => <> </>
}));
const DashboardFrame = props => {
  const $ = _c(153);
  const theme = useTheme();
  const {
    t
  } = useTranslation("common");
  const {
    toggleCopilot,
    closeCopilotDrawer
  } = useCopilot();
  const {
    value: canViewCopilotSidenav
  } = useFeatureFlag(FEATURE_FLAGS.copilotSidenav);
  const {
    value: canViewCopilotFullscreen
  } = useFeatureFlag(FEATURE_FLAGS.copilot);
  const {
    currentUser,
    logOut
  } = useAuth();
  const {
    data: currentUserDetails
  } = useQueryUserProfile(currentUser?.id);
  const {
    currentClient,
    isValidClient,
    error
  } = useCurrentClient();
  const shouldShowInternalFeatures = useShouldShowInternalFeatures();
  const [open, setIsOpen] = useState(false);
  const [isSwitchWorkspace, setIsSwitchWorkspace] = useState(false);
  const [isUserProfile, setIsUserProfile] = useState(false);
  const [isAdminWorkspaceProfile, setIsAdminWorkspaceProfile] = useState(false);
  const [isNotifications, setIsNotifications] = useState(false);
  const {
    checkClientAccessToFeature
  } = useHasClientFeatureAccess();
  const {
    isPreloaded,
    isSuccess: areAllPreloadedRequestsSuccessful
  } = useDashboardDataPreloader();
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();
  const {
    client: featureFlagClient
  } = useFeatureFlagClient();
  const isApiLoading = isFetching > 0 || isMutating > 0;
  const {
    pathnameWithQueryParams
  } = usePathname();
  const breadcrumbs = useBreadcrumbs();
  const navButtons = useNavButtons();
  const reload = useReload();
  const isOnCopilotStandalonePage = useIsCopilotStandaloneChatPage();
  useDashboardFrameKeybindings();
  let t0;
  let t1;
  if ($[0] !== closeCopilotDrawer || $[1] !== isOnCopilotStandalonePage) {
    t0 = () => {
      if (isOnCopilotStandalonePage) {
        closeCopilotDrawer();
      }
    };
    t1 = [isOnCopilotStandalonePage, closeCopilotDrawer];
    $[0] = closeCopilotDrawer;
    $[1] = isOnCopilotStandalonePage;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  const [isSideNavExpanded, setIsSideNavExpanded] = useState(_temp);
  let t2;
  if ($[4] !== isSideNavExpanded) {
    t2 = () => {
      store(LOCAL_STORAGE_KEYS.SIDE_NAVIGATION_STATE, (!isSideNavExpanded).toString());
      setIsSideNavExpanded(!isSideNavExpanded);
    };
    $[4] = isSideNavExpanded;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleSideNavToggle = t2;
  let t3;
  let t4;
  if ($[6] !== currentClient || $[7] !== currentUser) {
    t3 = () => {
      if (Boolean(currentClient.id) && currentUser?.id != null) {
        logRocketIdentify({
          user: currentUser,
          client: currentClient
        });
      }
    };
    t4 = [currentClient, currentUser];
    $[6] = currentClient;
    $[7] = currentUser;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== areAllPreloadedRequestsSuccessful || $[11] !== currentClient || $[12] !== currentUserDetails || $[13] !== featureFlagClient || $[14] !== isPreloaded || $[15] !== shouldShowInternalFeatures) {
    t5 = () => {
      if (isPreloaded && areAllPreloadedRequestsSuccessful && currentUserDetails != null) {
        bootIntercom({
          user: currentUserDetails,
          client: currentClient
        });
        updateIntercom({
          user: currentUserDetails,
          client: currentClient
        });
        featureFlagClient?.updateUserAsync({
          userID: currentUserDetails.id.toString(),
          custom: {
            clientId: currentClient.id,
            clientName: currentClient.name,
            hasInternalFeatureAccess: shouldShowInternalFeatures
          },
          privateAttributes: {
            email: currentUserDetails.email
          }
        });
      }
    };
    $[10] = areAllPreloadedRequestsSuccessful;
    $[11] = currentClient;
    $[12] = currentUserDetails;
    $[13] = featureFlagClient;
    $[14] = isPreloaded;
    $[15] = shouldShowInternalFeatures;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== areAllPreloadedRequestsSuccessful || $[18] !== currentClient || $[19] !== currentUser || $[20] !== currentUserDetails || $[21] !== featureFlagClient || $[22] !== isPreloaded || $[23] !== shouldShowInternalFeatures) {
    t6 = [currentUserDetails, shouldShowInternalFeatures, currentClient, areAllPreloadedRequestsSuccessful, isPreloaded, featureFlagClient, currentUser];
    $[17] = areAllPreloadedRequestsSuccessful;
    $[18] = currentClient;
    $[19] = currentUser;
    $[20] = currentUserDetails;
    $[21] = featureFlagClient;
    $[22] = isPreloaded;
    $[23] = shouldShowInternalFeatures;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  useEffect(t5, t6);
  const {
    data: t7
  } = useQueryUnreadNotificationsCount(currentClient.id, isValidClient && !isNotifications);
  let t8;
  if ($[25] !== t7) {
    t8 = t7 === undefined ? {
      count: 0
    } : t7;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const notificationCount = t8;
  let t9;
  if ($[27] !== checkClientAccessToFeature) {
    t9 = featureIds => {
      const hasAccess = featureIds.some(featureId => checkClientAccessToFeature({
        featureId
      })?.hasAccess === true);
      return !hasAccess;
    };
    $[27] = checkClientAccessToFeature;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const isSideNavItemLocked = t9;
  let t10;
  if ($[29] !== canViewCopilotFullscreen || $[30] !== isSideNavItemLocked || $[31] !== shouldShowInternalFeatures || $[32] !== t) {
    t10 = buildNavContentItems({
      t,
      isSideNavItemLocked,
      shouldShowInternalFeatures,
      canViewCopilotFullscreen
    });
    $[29] = canViewCopilotFullscreen;
    $[30] = isSideNavItemLocked;
    $[31] = shouldShowInternalFeatures;
    $[32] = t;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const navContentItems = t10;
  let defaultNavArgs;
  let handleBackToOverview;
  let handleSwitchWorkspaceFromFlyout;
  let t11;
  if ($[34] !== breadcrumbs || $[35] !== canViewCopilotSidenav || $[36] !== currentClient || $[37] !== currentUser?.email || $[38] !== currentUser?.id || $[39] !== currentUser?.name || $[40] !== currentUserDetails || $[41] !== handleSideNavToggle || $[42] !== isOnCopilotStandalonePage || $[43] !== isSideNavExpanded || $[44] !== isValidClient || $[45] !== logOut || $[46] !== navButtons || $[47] !== navContentItems || $[48] !== notificationCount.count || $[49] !== pathnameWithQueryParams || $[50] !== reload || $[51] !== t || $[52] !== toggleCopilot) {
    const {
      primaryIndex,
      childIndex
    } = getPathNameIndexes(pathnameWithQueryParams, navContentItems);
    let t12;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = () => {
        setIsAdminWorkspaceProfile(false);
      };
      $[57] = t12;
    } else {
      t12 = $[57];
    }
    handleBackToOverview = t12;
    let t13;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = () => {
        setIsSwitchWorkspace(true);
      };
      $[58] = t13;
    } else {
      t13 = $[58];
    }
    handleSwitchWorkspaceFromFlyout = t13;
    let t14;
    if ($[59] !== currentUserDetails) {
      t14 = <AvatarWithLabel user={currentUserDetails} />;
      $[59] = currentUserDetails;
      $[60] = t14;
    } else {
      t14 = $[60];
    }
    let t15;
    if ($[61] !== currentUser?.id) {
      t15 = () => {
        OverScreen.show("profileFlyout", {
          id: currentUser?.id
        });
      };
      $[61] = currentUser?.id;
      $[62] = t15;
    } else {
      t15 = $[62];
    }
    let t16;
    if ($[63] !== logOut || $[64] !== reload) {
      t16 = () => {
        logOut();
        reload();
      };
      $[63] = logOut;
      $[64] = reload;
      $[65] = t16;
    } else {
      t16 = $[65];
    }
    let t17;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <BookIcon />;
      $[66] = t17;
    } else {
      t17 = $[66];
    }
    const defaultSideArgs = {
      onToogleClick: handleSideNavToggle,
      isExpanded: isSideNavExpanded,
      navContent: {
        content: navContentItems,
        selectedIndex: primaryIndex,
        selectedChildIndex: childIndex
      },
      navFooter: {
        AvatarComponent: t14,
        accountActionslist: [{
          label: t("sideNavigation.publicProfile"),
          handleClick: t15
        }, {
          label: t("sideNavigation.logout"),
          handleClick: t16
        }]
      },
      navFooterActions: [{
        label: HELP_CENTER_LABEL,
        handleClick: openIntercomSpace(INTERCOM_SPACE_IDS.HELP),
        IconComponent: t17
      }],
      navHeader: {
        Link: _temp2,
        label: NAV_HEADER_LABEL
      }
    };
    const t18 = currentClient.name === EMPTY_CLIENT_NAME ? undefined : currentClient.name;
    let t19;
    if ($[67] !== currentClient || $[68] !== isValidClient) {
      t19 = <AvatarWithLabel {...isValidClient ? {
        client: currentClient
      } : {}} />;
      $[67] = currentClient;
      $[68] = isValidClient;
      $[69] = t19;
    } else {
      t19 = $[69];
    }
    let t20;
    if ($[70] !== isValidClient) {
      t20 = () => {
        if (isValidClient) {
          setIsOpen(true);
          setIsSwitchWorkspace(false);
          setIsNotifications(false);
          setIsUserProfile(false);
        }
      };
      $[70] = isValidClient;
      $[71] = t20;
    } else {
      t20 = $[71];
    }
    let t21;
    if ($[72] !== currentClient.url || $[73] !== t18 || $[74] !== t19 || $[75] !== t20) {
      t21 = {
        name: t18,
        url: currentClient.url,
        IconComponent: t19,
        handleOnClick: t20
      };
      $[72] = currentClient.url;
      $[73] = t18;
      $[74] = t19;
      $[75] = t20;
      $[76] = t21;
    } else {
      t21 = $[76];
    }
    let t22;
    let t23;
    if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <SwitchIcon />;
      t23 = () => {
        setIsNotifications(false);
        setIsSwitchWorkspace(true);
        setIsOpen(true);
      };
      $[77] = t22;
      $[78] = t23;
    } else {
      t22 = $[77];
      t23 = $[78];
    }
    let t24;
    if ($[79] !== t) {
      t24 = t("topNavigation.switchWorkspaceTooltip");
      $[79] = t;
      $[80] = t24;
    } else {
      t24 = $[80];
    }
    let t25;
    if ($[81] !== t24) {
      t25 = {
        IconComponent: t22,
        onClick: t23,
        tooltipProps: {
          title: t24
        },
        ariaLabel: "clients"
      };
      $[81] = t24;
      $[82] = t25;
    } else {
      t25 = $[82];
    }
    let t26;
    if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <FeedIcon />;
      $[83] = t26;
    } else {
      t26 = $[83];
    }
    let t27;
    if ($[84] !== notificationCount.count || $[85] !== t) {
      t27 = formatNotificationBadgeValue(notificationCount.count, t);
      $[84] = notificationCount.count;
      $[85] = t;
      $[86] = t27;
    } else {
      t27 = $[86];
    }
    let t28;
    if ($[87] !== t27) {
      t28 = {
        value: t27,
        variant: NOTIFICATION_VARIANT_FILLED
      };
      $[87] = t27;
      $[88] = t28;
    } else {
      t28 = $[88];
    }
    let t29;
    if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = () => {
        setIsNotifications(true);
        setIsOpen(true);
        setIsSwitchWorkspace(false);
        setIsUserProfile(false);
      };
      $[89] = t29;
    } else {
      t29 = $[89];
    }
    let t30;
    if ($[90] !== t) {
      t30 = t("topNavigation.notificationsTooltip");
      $[90] = t;
      $[91] = t30;
    } else {
      t30 = $[91];
    }
    let t31;
    if ($[92] !== t30) {
      t31 = {
        title: t30
      };
      $[92] = t30;
      $[93] = t31;
    } else {
      t31 = $[93];
    }
    let t32;
    if ($[94] !== t28 || $[95] !== t31) {
      t32 = [{
        IconComponent: t26,
        notificationProps: t28,
        onClick: t29,
        tooltipProps: t31,
        ariaLabel: "notifications"
      }];
      $[94] = t28;
      $[95] = t31;
      $[96] = t32;
    } else {
      t32 = $[96];
    }
    let t33;
    let t34;
    if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <BookIcon />;
      t34 = openIntercomSpace(INTERCOM_SPACE_IDS.HELP);
      $[97] = t33;
      $[98] = t34;
    } else {
      t33 = $[97];
      t34 = $[98];
    }
    let t35;
    if ($[99] !== t) {
      t35 = t("topNavigation.helpTooltip");
      $[99] = t;
      $[100] = t35;
    } else {
      t35 = $[100];
    }
    let t36;
    if ($[101] !== t35) {
      t36 = {
        IconComponent: t33,
        onClick: t34,
        tooltipProps: {
          title: t35
        },
        ariaLabel: "help"
      };
      $[101] = t35;
      $[102] = t36;
    } else {
      t36 = $[102];
    }
    let t37;
    if ($[103] !== canViewCopilotSidenav || $[104] !== isOnCopilotStandalonePage || $[105] !== t || $[106] !== toggleCopilot) {
      t37 = canViewCopilotSidenav ? [{
        IconComponent: <CopilotIcon />,
        onClick: () => {
          toggleCopilot();
        },
        ariaLabel: COPILOT_ARIA_LABEL,
        disabled: isOnCopilotStandalonePage,
        tooltipProps: {
          title: isOnCopilotStandalonePage ? t("topNavigation.copilotDisabledOnCopilotPageTooltip") : t("topNavigation.copilotTooltip")
        }
      }] : [];
      $[103] = canViewCopilotSidenav;
      $[104] = isOnCopilotStandalonePage;
      $[105] = t;
      $[106] = toggleCopilot;
      $[107] = t37;
    } else {
      t37 = $[107];
    }
    let t38;
    if ($[108] !== t25 || $[109] !== t32 || $[110] !== t36 || $[111] !== t37) {
      t38 = [t25, ...t32, t36, ...t37];
      $[108] = t25;
      $[109] = t32;
      $[110] = t36;
      $[111] = t37;
      $[112] = t38;
    } else {
      t38 = $[112];
    }
    let t39;
    if ($[113] !== breadcrumbs || $[114] !== navButtons || $[115] !== t21 || $[116] !== t38) {
      t39 = {
        clientSection: t21,
        navActionsSection: t38,
        breadcrumbs,
        navButtons
      };
      $[113] = breadcrumbs;
      $[114] = navButtons;
      $[115] = t21;
      $[116] = t38;
      $[117] = t39;
    } else {
      t39 = $[117];
    }
    defaultNavArgs = t39;
    let t40;
    if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
      t40 = <Avatar kind={AvatarKind.label} label={AVATAR_LABEL} />;
      $[118] = t40;
    } else {
      t40 = $[118];
    }
    t11 = Object.assign(defaultSideArgs, {
      navFooter: Object.assign(defaultSideArgs.navFooter, {
        isExpanded: IS_NAV_FOOTER_EXPANDED,
        email: currentUser?.email ?? EMPTY_STRING,
        fullName: currentUser?.name ?? EMPTY_STRING,
        isNavExpanded: IS_NAV_EXPANDED,
        IconComponent: t40
      })
    });
    $[34] = breadcrumbs;
    $[35] = canViewCopilotSidenav;
    $[36] = currentClient;
    $[37] = currentUser?.email;
    $[38] = currentUser?.id;
    $[39] = currentUser?.name;
    $[40] = currentUserDetails;
    $[41] = handleSideNavToggle;
    $[42] = isOnCopilotStandalonePage;
    $[43] = isSideNavExpanded;
    $[44] = isValidClient;
    $[45] = logOut;
    $[46] = navButtons;
    $[47] = navContentItems;
    $[48] = notificationCount.count;
    $[49] = pathnameWithQueryParams;
    $[50] = reload;
    $[51] = t;
    $[52] = toggleCopilot;
    $[53] = defaultNavArgs;
    $[54] = handleBackToOverview;
    $[55] = handleSwitchWorkspaceFromFlyout;
    $[56] = t11;
  } else {
    defaultNavArgs = $[53];
    handleBackToOverview = $[54];
    handleSwitchWorkspaceFromFlyout = $[55];
    t11 = $[56];
  }
  const sideArgs = t11;
  let t12;
  if ($[119] !== sideArgs) {
    t12 = <SideNavigation {...sideArgs} />;
    $[119] = sideArgs;
    $[120] = t12;
  } else {
    t12 = $[120];
  }
  const SNavigation = t12;
  let t13;
  if ($[121] !== defaultNavArgs) {
    t13 = <TopNavigation {...defaultNavArgs} />;
    $[121] = defaultNavArgs;
    $[122] = t13;
  } else {
    t13 = $[122];
  }
  const TNavigation = t13;
  let t14;
  if ($[123] !== canViewCopilotSidenav) {
    t14 = canViewCopilotSidenav ? <CopilotDrawer /> : undefined;
    $[123] = canViewCopilotSidenav;
    $[124] = t14;
  } else {
    t14 = $[124];
  }
  const BDrawer = t14;
  let t15;
  if ($[125] !== checkClientAccessToFeature || $[126] !== pathnameWithQueryParams) {
    t15 = checkClientAccessToFeature({
      route: pathnameWithQueryParams
    });
    $[125] = checkClientAccessToFeature;
    $[126] = pathnameWithQueryParams;
    $[127] = t15;
  } else {
    t15 = $[127];
  }
  const clientAccessToFeature = t15;
  const isValidClientWithAccess = isValidClient && clientAccessToFeature?.hasAccess !== false;
  const overScreenContext = useOverscreenContext();
  const prevPathnameRef = useRef(pathnameWithQueryParams);
  let t16;
  let t17;
  if ($[128] !== overScreenContext || $[129] !== pathnameWithQueryParams) {
    t16 = () => {
      if (prevPathnameRef.current !== pathnameWithQueryParams) {
        clearOverscreensOnRouteChange(pathnameWithQueryParams, overScreenContext);
        prevPathnameRef.current = pathnameWithQueryParams;
      }
    };
    t17 = [pathnameWithQueryParams, overScreenContext];
    $[128] = overScreenContext;
    $[129] = pathnameWithQueryParams;
    $[130] = t16;
    $[131] = t17;
  } else {
    t16 = $[130];
    t17 = $[131];
  }
  useEffect(t16, t17);
  let t18;
  if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setIsOpen(false);
      setIsNotifications(false);
    };
    $[132] = t18;
  } else {
    t18 = $[132];
  }
  const handleFlyoutClose = t18;
  let t19;
  if ($[133] !== BDrawer || $[134] !== SNavigation || $[135] !== TNavigation || $[136] !== clientAccessToFeature || $[137] !== error || $[138] !== handleBackToOverview || $[139] !== handleSwitchWorkspaceFromFlyout || $[140] !== isAdminWorkspaceProfile || $[141] !== isApiLoading || $[142] !== isNotifications || $[143] !== isOnCopilotStandalonePage || $[144] !== isPreloaded || $[145] !== isSwitchWorkspace || $[146] !== isUserProfile || $[147] !== isValidClientWithAccess || $[148] !== open || $[149] !== props.children || $[150] !== t || $[151] !== theme) {
    const flyoutTopLeftAdornmentProps = {
      isAdminWorkspaceProfile,
      isUserProfile,
      isSwitchWorkspace,
      isNotifications,
      onBackToOverview: handleBackToOverview,
      onSwitchWorkspace: handleSwitchWorkspaceFromFlyout,
      t
    };
    t19 = !isPreloaded ? <ProgressBarLoader /> : <><Flyout onClose={handleFlyoutClose} isExpanded={open} topLeftAdornment={shouldShowFlyoutTopLeftAdornment(flyoutTopLeftAdornmentProps) ? <FlyoutTopLeftAdornment {...flyoutTopLeftAdornmentProps} /> : undefined}><FlyoutContent isSwitchWorkspace={isSwitchWorkspace} isUserProfile={isUserProfile} isNotifications={isNotifications} setIsUserProfile={setIsUserProfile} handleFlyout={setIsOpen} isAdminWorkspaceProfile={isAdminWorkspaceProfile} setIsAdminWorkspaceProfile={setIsAdminWorkspaceProfile} /></Flyout>{config("APP_ENV") === STAGING_PROD_ENV && <Grid container={true} height={STAGING_WARNING_HEIGHT} width={STAGING_WARNING_WIDTH} top={STAGING_WARNING_TOP} textAlign="center" justifyContent="center" alignItems="center" position="absolute" zIndex={STAGING_WARNING_Z_INDEX} boxShadow={theme.elevation.mediumDepth} style={{
        backgroundColor: theme.tokens.background.semantic.error.tonal.superSoft.default
      }}><Typography variant="h5" color="error">{STAGING_WARNING_TEXT}</Typography></Grid>}<FrameWithAuthAndInternalAccessCheck contentPadding={isOnCopilotStandalonePage ? `0 ${FRAME_CONTENT_PADDING}` : undefined} {...{
        SideNavigation: SNavigation,
        TopNavigation: TNavigation,
        BodyDrawer: BDrawer
      }}><GlobalLoader isLoading={isApiLoading} />{isValidClientWithAccess && <ErrorReporterProvider FallbackUI={_temp3}><NotFoundErrorBoundary>{props.children}</NotFoundErrorBoundary></ErrorReporterProvider>}{error == null && clientAccessToFeature?.hasAccess === false && <NoAccessToFeature featureId={clientAccessToFeature.featureId} />}{error != null && <ErrInvalidClient error={error} />}</FrameWithAuthAndInternalAccessCheck></>;
    $[133] = BDrawer;
    $[134] = SNavigation;
    $[135] = TNavigation;
    $[136] = clientAccessToFeature;
    $[137] = error;
    $[138] = handleBackToOverview;
    $[139] = handleSwitchWorkspaceFromFlyout;
    $[140] = isAdminWorkspaceProfile;
    $[141] = isApiLoading;
    $[142] = isNotifications;
    $[143] = isOnCopilotStandalonePage;
    $[144] = isPreloaded;
    $[145] = isSwitchWorkspace;
    $[146] = isUserProfile;
    $[147] = isValidClientWithAccess;
    $[148] = open;
    $[149] = props.children;
    $[150] = t;
    $[151] = theme;
    $[152] = t19;
  } else {
    t19 = $[152];
  }
  return t19;
};
export default DashboardFrame;
function _temp() {
  return get(LOCAL_STORAGE_KEYS.SIDE_NAVIGATION_STATE) == null ? true : JSON.parse(get(LOCAL_STORAGE_KEYS.SIDE_NAVIGATION_STATE) as string);
}
function _temp2(props_0) {
  return <Link href={HOME_PATH}>{props_0.children}</Link>;
}
function _temp3(t0) {
  const {
    error: error_0,
    resetError
  } = t0;
  return <ErrorFallback error={error_0} resetError={resetError} hasDashboardFrame={true} />;
}